/**
 * community/shared.tsx — presentational pieces shared by the three Community Hub pages.
 *
 * This is a PROVISIONAL engineering UI. There is no Will UX flow for the Community Hub (his seven
 * SVGs cover only tools/profile/search), so the backend is final but this frontend is a working
 * shell his flows will later refine. Every landing carries a visible provisional banner so that
 * status is explicit, never mistaken for a signed-off design.
 *
 * Style approach mirrors tools/shared.tsx: one inline <style> block, class names local to these
 * pages, reusing the frame's global --tb-* tokens (App.tsx :root). No design polish, no new
 * dependency — just legible, labelled, accessible controls. We reuse the tools slice's SignedOut
 * verbatim (same wording family, same backend login round-trip); the community family is always
 * mounted, so there is no feature-disabled state to import.
 */
import { Link } from 'react-router';
import { CHANNELS } from '@trailblazer/shared';
import { monogram, resolveAvatar } from '../avatar';

// Re-export the signed-out prompt so the community pages import their shared states from one place.
export { SignedOut } from '../tools/shared';

/**
 * The channel metadata the pages read to map a URL slug to the numeric id the API expects. Keys are
 * widened to plain string/number (CHANNELS is `as const`, so its keys are a literal union) so an
 * arbitrary route param can be looked up without a cast. This is NOT an authority on visibility —
 * the backend decides what the caller may read; this only names a channel we already know about.
 */
export interface ChannelMeta {
  id: number;
  slug: string;
  name: string;
}
export const CHANNELS_BY_SLUG: ReadonlyMap<string, ChannelMeta> = new Map(
  CHANNELS.map((c) => [c.slug as string, { id: c.id, slug: c.slug, name: c.name }]),
);
export const CHANNELS_BY_ID: ReadonlyMap<number, ChannelMeta> = new Map(
  CHANNELS.map((c) => [c.id as number, { id: c.id, slug: c.slug, name: c.name }]),
);

/**
 * Community-specific styles. Threads, the nested post tree (indentation per depth), the composer,
 * and the per-post action row. Built over the frame's tokens so it sits inside the app shell without
 * a theme of its own. `.tb-community` scopes every rule so it never leaks into other surfaces.
 */
export const COMMUNITY_STYLES = `
  .tb-community { max-width: 60rem; }
  .tb-community h1 { margin-top: 0; }
  .tb-community a { color: var(--tb-accent); }
  .tb-community .tb-muted { color: var(--tb-muted); }
  .tb-community .tb-crumbs { font-size: 0.85rem; margin: 0 0 1rem; }

  /* The provisional-status banner — a warn-toned note, present on every community landing. */
  .tb-community .tb-provisional {
    border: 1px solid var(--tb-line);
    border-left: 4px solid var(--tb-banner-line);
    background: var(--tb-banner-bg);
    color: var(--tb-banner-ink);
    padding: 0.6rem 0.9rem;
    margin: 0 0 1.25rem;
    border-radius: 4px;
    font-size: 0.9rem;
  }

  /* Channel list (landing) + thread list (channel) share a plain card list. */
  .tb-community ul.tb-list { list-style: none; margin: 1rem 0; padding: 0; }
  .tb-community ul.tb-list > li {
    border: 1px solid var(--tb-line);
    border-radius: 6px;
    padding: 0.6rem 0.9rem;
    margin: 0.5rem 0;
    background: var(--tb-surface);
  }
  .tb-community .tb-list-title { font-weight: 600; font-size: 1rem; }
  .tb-community .tb-list-meta { color: var(--tb-muted); font-size: 0.85rem; margin-top: 0.2rem; }

  /* Role markers beside a name (CH-F05) — small, muted pills, no colour claim of authority. */
  .tb-community .tb-marker {
    display: inline-block;
    border: 1px solid var(--tb-line);
    border-radius: 999px;
    padding: 0 0.45rem;
    margin-left: 0.4rem;
    font-size: 0.72rem;
    font-weight: 600;
    color: var(--tb-muted);
    vertical-align: middle;
  }

  /* The nested post tree. Each level indents; a left rule shows the nesting (NFR02: also aria-level). */
  .tb-community .tb-post {
    border-left: 2px solid var(--tb-line);
    padding: 0.5rem 0 0.5rem 0.9rem;
    margin: 0.6rem 0 0;
  }
  /* One-shot flash when a reply notification's link points at this exact comment: a brief blue
     wash that fades back to normal over ~1s (JS clears the class after the same duration). */
  .tb-community .tb-post--flash { animation: tb-post-flash 1s ease-out; border-radius: 6px; }
  @keyframes tb-post-flash {
    0% { background: color-mix(in srgb, var(--tb-accent) 18%, transparent); }
    100% { background: transparent; }
  }
  .tb-community .tb-post-head { font-size: 0.85rem; margin-bottom: 0.3rem; }
  .tb-community .tb-post-author { font-weight: 600; }

  /* The author's avatar next to their name. Sized to the username row (~1.4× the 0.85rem head text):
     small, clearly visible, not visually dominant. Sits on the text baseline via vertical-align, so
     the name + markers + timestamp still read as one line. The fallback monogram shares the size. */
  .tb-community .tb-avatar {
    display: inline-block;
    width: 1.4em; height: 1.4em;
    border-radius: 50%; object-fit: cover;
    border: 1px solid var(--tb-line); background: var(--tb-surface);
    vertical-align: -0.35em; margin-right: 0.4rem;
  }
  .tb-community .tb-avatar--fallback {
    display: inline-flex; align-items: center; justify-content: center;
    font-size: 0.7em; font-weight: 700; line-height: 1;
    color: var(--tb-accent-ink); background: var(--tb-accent);
  }
  .tb-community .tb-post-body { white-space: pre-wrap; overflow-wrap: anywhere; margin: 0.2rem 0; }
  .tb-community .tb-post-body--removed { color: var(--tb-muted); font-style: italic; }
  /* The thread's own description — deliberately distinct from a comment's .tb-post-body: no
     border/action row sits under it, so it never reads as something you could reply/like/report. */
  .tb-community .tb-thread-body {
    white-space: pre-wrap; overflow-wrap: anywhere;
    margin: 0.5rem 0 1rem; max-width: 70ch;
  }
  .tb-community .tb-post-actions { margin-top: 0.35rem; display: flex; flex-wrap: wrap; gap: 0.5rem; }

  /* Action buttons: plain text-buttons so a post's row is not a wall of accent blocks. The
     primary submit buttons (post a thread / reply) keep the accent fill from the tokens. */
  .tb-community button.tb-action {
    font: inherit; font-size: 0.85rem; line-height: 1;
    padding: 0.25rem 0.5rem;
    border: 1px solid var(--tb-line); border-radius: 6px;
    background: var(--tb-bg); color: var(--tb-ink); cursor: pointer;
  }
  .tb-community button.tb-action:hover { border-color: var(--tb-accent); }
  .tb-community button.tb-action[aria-pressed="true"] { border-color: var(--tb-accent); color: var(--tb-accent); font-weight: 600; }
  .tb-community button.tb-action:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Composer + reply/edit boxes. */
  .tb-community fieldset { border: 1px solid var(--tb-line); border-radius: 6px; margin: 1rem 0; padding: 0.75rem 1rem; }
  .tb-community legend { font-weight: 600; padding: 0 0.4rem; }
  .tb-community label.tb-field { display: block; margin: 0.6rem 0; }
  .tb-community label.tb-field > span { display: block; font-weight: 600; margin-bottom: 0.2rem; }
  .tb-community input[type="text"], .tb-community textarea, .tb-community select {
    width: 100%; font: inherit;
    padding: 0.4rem 0.5rem;
    border: 1px solid var(--tb-line); border-radius: 6px;
    background: var(--tb-bg); color: var(--tb-ink);
  }
  .tb-community textarea { min-height: 4rem; resize: vertical; }
  .tb-community button.tb-submit {
    font: inherit; padding: 0.5rem 1rem;
    border: 1px solid var(--tb-accent); border-radius: 6px;
    background: var(--tb-accent); color: var(--tb-accent-ink); cursor: pointer;
  }
  .tb-community button.tb-submit:disabled { opacity: 0.5; cursor: not-allowed; }
  .tb-community .tb-inline-form { margin: 0.5rem 0 0; }
  .tb-community .tb-err { color: var(--tb-banner-ink); background: var(--tb-banner-bg);
    border: 1px solid var(--tb-banner-line); border-radius: 4px; padding: 0.4rem 0.6rem; margin: 0.5rem 0; font-size: 0.85rem; }
  .tb-community .tb-order { margin: 1rem 0; font-size: 0.9rem; }
  .tb-community .tb-order a { margin-right: 0.75rem; }
  .tb-community .tb-order a[aria-current="true"] { font-weight: 700; text-decoration: none; color: var(--tb-ink); }
`;

/** The always-present provisional-status banner. Names no future capability (PC-F14) — it states
 *  only that this working UI is engineering-provisional, pending the product's own flow. */
export function ProvisionalBanner() {
  return (
    <p className="tb-provisional" role="note">
      <strong>Provisional interface.</strong> This Community Hub screen is a working engineering
      build. Its layout and wording are not the final design and will be refined.
    </p>
  );
}

/** Format an ISO timestamp as a short local date-time, or an em dash when null/unparseable. */
export function shortWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString();
}

/**
 * A coarse "how long ago" for thread activity — the exact timestamp is noise in a list you scan, so
 * this rounds DOWN to the largest whole unit and climbs a fixed ladder, each unit rolling into the
 * next as it fills:
 *   under a minute   → "just now"
 *   1–59 minutes     → "N minutes ago"   (60 min → 1 hour)
 *   1–23 hours       → "N hours ago"     (24 h  → 1 day)
 *   1–6 days         → "N days ago"      (7 d   → 1 week)
 *   1–3 weeks        → "N weeks ago"     (4 wk  → 1 month)
 *   1–11 months      → "N months ago"    (12 mo → 1 year)
 *   1 year or older  → "More than 1 year ago"   (the ceiling — we never count higher)
 * A week is 7 days, a month is taken as 4 weeks (28 days) and a year as 12 of those months (336
 * days), so each rung rolls into the next exactly as it fills — 4 weeks reads "1 month", 12 months
 * reads "More than 1 year ago" — rather than being calendar-exact. Null/unparseable is the em dash,
 * matching shortWhen; a future timestamp (clock skew) reads "just now" rather than a negative count.
 */
export function relativeWhen(iso: string | null): string {
  if (!iso) return '—';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';

  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60) return 'just now';

  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${String(mins)} ${mins === 1 ? 'minute' : 'minutes'} ago`;

  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${String(hours)} ${hours === 1 ? 'hour' : 'hours'} ago`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${String(days)} ${days === 1 ? 'day' : 'days'} ago`;

  const weeks = Math.floor(days / 7);
  if (weeks < 4) return `${String(weeks)} ${weeks === 1 ? 'week' : 'weeks'} ago`;

  const months = Math.floor(days / 28);
  if (months < 12) return `${String(months)} ${months === 1 ? 'month' : 'months'} ago`;

  return 'More than 1 year ago';
}

/**
 * An author's display name, linked to their profile (PR-F01). Clicking a name opens that person's
 * profile in a NEW TAB (target="_blank"), so the reader keeps their place in the thread. The link
 * target depends on WHOSE name it is:
 *   - the caller's own name (`isOwn`) → their private profile (/me), the full owner view.
 *   - anyone else → that person's PUBLIC profile (/users/:userId), the read-only §3.2-public view.
 *
 * A departed author (name === null) is the neutral marker (CH-F04) and is NOT a link — there is no
 * one to visit, and their id is already suppressed upstream. A live author with a null id (should
 * not happen) also falls back to the plain name rather than a broken link.
 *
 * NOTE: opening in a new tab is a product decision that may change; this is the single place that
 * behaviour lives, so a future switch to same-tab navigation is a one-line change here.
 */
export function AuthorName({
  name,
  userId,
  isOwn,
}: {
  name: string | null;
  userId: string | null;
  isOwn: boolean;
}) {
  if (name === null) return <span className="tb-muted">[former colleague]</span>;
  if (userId === null) return <span className="tb-post-author">{name}</span>;
  const to = isOwn ? '/me' : `/users/${userId}`;
  return (
    <Link className="tb-post-author" to={to} target="_blank" rel="noopener noreferrer">
      {name}
    </Link>
  );
}

/**
 * The author's avatar, shown immediately before their name on a post (a small, recognisable image
 * roughly the height of the username text). The stored ref is resolved through the SAME allowlist
 * chokepoint the profile pages use (avatar.ts resolveAvatar): a stock pick maps to a bundled asset,
 * a validated upload data: URI is emitted as-is, and anything else falls through to the directory
 * photo — else null, where we render a monogram. A hostile ref can therefore never smuggle a URL
 * into <img src>. A departed author (name === null, refs already suppressed upstream) shows only the
 * monogram of the neutral marker's initial, aria-hidden so screen readers hear the name text alone.
 */
export function AuthorAvatar({
  name,
  avatarKind,
  avatarBlobRef,
  photoBlobRef,
}: {
  name: string | null;
  avatarKind: string | null;
  avatarBlobRef: string | null;
  photoBlobRef: string | null;
}) {
  const src = resolveAvatar({ avatarKind, avatarBlobRef }, photoBlobRef);
  if (src) {
    return (
      // eslint-disable-next-line jsx-a11y/img-redundant-alt -- alt is a name, intentional
      <img className="tb-avatar" src={src} alt={name ? `${name}'s profile photo` : ''} />
    );
  }
  return (
    <span className="tb-avatar tb-avatar--fallback" aria-hidden="true">
      {monogram(name ?? '?')}
    </span>
  );
}

/** The genuinely-held role markers beside a name (CH-F05). Rendered only when actually present. */
export function Markers({ champion, verified }: { champion: boolean; verified: boolean }) {
  return (
    <>
      {champion ? <span className="tb-marker">Champion</span> : null}
      {verified ? <span className="tb-marker">SWAT</span> : null}
    </>
  );
}
